import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    ComplexModalTemplate,
    FIRST_COMPLEX_MODAL_STEP,
} from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import {
    SelectFiberComponent,
    SelectFiberOption,
} from '@connect-services/client/components/select-fiber/select-fiber.component';
import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import {
    ColorEnum,
    FontColorEnum,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { EntityLocation, MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    BreakInformation,
    DataEntity,
    DepositsService,
    LayersService,
    OpticalFiber,
    SynopticService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, LoadingComponent, RessourcesModule, SelectComponent, SelectFiberComponent],
    selector: 'app-break-modal',
    templateUrl: './break-modal.component.html',
})
export class BreakModalComponent implements OnInit {
    protected breakDirectionOption: SelectOption<boolean>[] = [];
    protected readonly breakInformation = signal<BreakInformation | null>(null);
    protected readonly currentOpticalFibersOption = signal<SelectFiberOption[]>([]);
    protected readonly currentStep = signal<number>(1);
    protected data: { depositId: string; entityId: string; layerId: string } = inject(MAT_DIALOG_DATA);
    protected defaultBreakDirectionOption: SelectOption<boolean>[] = [];
    protected downStreamOpticalFibersOption: SelectFiberOption[] = [];
    protected readonly formGroup: WritableSignal<FormGroup<{ distance: FormControl<number | null> }>> = signal(
        new FormGroup({ distance: new FormControl<number | null>(null) }),
    );
    protected readonly isLoading = signal(false);
    protected readonly selectedBreakDirection = signal<boolean>(true);
    protected readonly selectedOpticalFiberOption = signal<SelectFiberOption | undefined>(undefined);
    protected upStreamOpticalFibersOption: SelectFiberOption[] = [];

    private readonly depositsService = inject(DepositsService);
    private readonly dialogRef = inject(MatDialogRef<BreakModalComponent>);
    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layerService = inject(LayersService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapHighlightService = inject(MapHighlightEntityService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly sourcesService = inject(SourcesService);
    private readonly synopticService = inject(SynopticService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.breakDirectionOption = [
            { label: this.translocoService.translate('downstreamToPBO'), value: false },
            { label: this.translocoService.translate('upstreamToNRO'), value: true },
        ];
        this.defaultBreakDirectionOption = [this.breakDirectionOption[0]];

        effect(async () => {
            if (this.currentStep() === FIRST_COMPLEX_MODAL_STEP) {
                return;
            }

            await this.findBreak();
        });
    }

    public async ngOnInit(): Promise<void> {
        await this.setOpticalFibers();

        this.currentOpticalFibersOption.set(this.upStreamOpticalFibersOption);
    }

    protected closeModal(modalResultEnum: ModalResultEnum): void {
        this.dialogRef.close(modalResultEnum);
    }

    protected selectBreakDirection(selectedOptions: boolean[]): void {
        const breakDirection = selectedOptions[0];

        this.selectedBreakDirection.set(breakDirection);

        this.currentOpticalFibersOption.set(
            breakDirection ? this.upStreamOpticalFibersOption : this.downStreamOpticalFibersOption,
        );
        this.selectedOpticalFiberOption.set(undefined);
    }

    protected selectOpticalFiber(selectedOption?: string): void {
        this.selectedOpticalFiberOption.set(
            this.currentOpticalFibersOption().find((option: SelectFiberOption) => option.value === selectedOption),
        );
    }

    private async centerAndHighlightOnNearestEntity(): Promise<void> {
        await this.layerListBloc.load({
            deposit: this.data.depositId,
            project: this.projectBloc.value()!.id,
            tableName: this.breakInformation()!.source_table,
        });

        if (!this.layerListBloc.value()?.items.length) {
            return;
        }

        const nearestLayerId = this.layerListBloc.value()!.items[0].id;

        const nearestLayerEntity: DataEntity = await firstValueFrom(
            this.layerService.layerControllerGetLayersEntity({
                entityId: this.breakInformation()!.uuid,
                id: nearestLayerId,
            }),
        );

        this.centerOnNearestEntity(nearestLayerEntity);

        this.highLightNearestEntity(nearestLayerId, nearestLayerEntity);
    }

    private centerOnNearestEntity(targetBreakLayerEntity: DataEntity): void {
        if (!targetBreakLayerEntity) {
            return;
        }

        let entityLocation: EntityLocation | null = null;

        if (targetBreakLayerEntity?.geom?.bbox && targetBreakLayerEntity?.geom?.type) {
            entityLocation = {
                bbox: targetBreakLayerEntity!.geom!.bbox,
                type: targetBreakLayerEntity!.geom!.type,
            };
        }

        this.mapDrawService.centerMapOnSpecificEntity(entityLocation);
    }

    private async findBreak(): Promise<void> {
        this.isLoading.set(true);

        this.breakInformation.set(
            await firstValueFrom(
                this.depositsService.depositControllerFindBreak({
                    breakDirection: this.selectedBreakDirection(),
                    distance: this.formGroup().value.distance ?? 0,
                    foCode: this.selectedOpticalFiberOption()!.value,
                    id: this.data.depositId,
                }),
            ),
        );

        this.isLoading.set(false);

        if (!this.breakInformation()) {
            return;
        }

        await this.centerAndHighlightOnNearestEntity();
    }

    private getStandardColor(colorCode: number): { color: ColorEnum; font: FontColorEnum } {
        const color = SynopticStandardUtils.getStandardColor(colorCode);

        return {
            color,
            font: SynopticStandardUtils.getFontColor(color),
        };
    }

    private highLightNearestEntity(layerId: string, entity: DataEntity): void {
        const source = this.sourcesService.getSource(layerId);

        if (!source) {
            return;
        }

        const entityType = this.mapHighlightService.getEntityTypeByEntity(entity.geom);

        this.mapHighlightService.manageHighlightFeature(source, [entity.uuid], entityType);
    }

    private mapOpticalFiberOption(opticalFiber: OpticalFiber): SelectFiberOption {
        const standardColorNumtub = this.getStandardColor(opticalFiber.fo_numtub ?? 1);
        const standardColorNintub = this.getStandardColor(opticalFiber.fo_nintub ?? 1);

        return {
            labelsWithStyle: [
                { label: opticalFiber?.cb_etiquet ?? '' },
                {
                    color: standardColorNumtub.color,
                    font: standardColorNumtub.font,
                    label: ` tube ${opticalFiber.fo_numtub}`,
                },
                {
                    color: standardColorNintub.color,
                    font: standardColorNintub.font,

                    label: ` fibre ${opticalFiber.fo_nintub}`,
                },
            ],
            searchLabel: `${opticalFiber.cb_etiquet} | tube ${opticalFiber.fo_numtub} | fibre ${opticalFiber.fo_nintub}`,
            value: opticalFiber.fo_code,
        };
    }

    private async setOpticalFibers(): Promise<void> {
        const upStreamOpticalFibers = await firstValueFrom(
            this.synopticService.synopticControllerGetOpticalFiber({
                breakDirection: true,
                depositId: this.data.depositId,
                entityId: this.data.entityId,
            }),
        );

        const downStreamOpticalFibers = await firstValueFrom(
            this.synopticService.synopticControllerGetOpticalFiber({
                breakDirection: false,
                depositId: this.data.depositId,
                entityId: this.data.entityId,
            }),
        );

        this.setOpticalFibersOptions(upStreamOpticalFibers, downStreamOpticalFibers);
    }

    private setOpticalFibersOptions(
        upStreamOpticalFibers: OpticalFiber[],
        downStreamOpticalFibers: OpticalFiber[],
    ): void {
        this.upStreamOpticalFibersOption = upStreamOpticalFibers.map(this.mapOpticalFiberOption.bind(this));

        this.downStreamOpticalFibersOption = downStreamOpticalFibers.map(this.mapOpticalFiberOption.bind(this));
    }
}
